import { useTheme } from '@mui/material/styles';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import { Placeholder } from '@sitecore-jss/sitecore-jss-nextjs';
import { isEditorActive } from '@sitecore-jss/sitecore-jss-nextjs/utils';
import { StAppBarWrapper } from './components/DTP/HeaderNavigation/HeaderNavigation.styles';
import isEmpty from 'lodash/isEmpty';
import React, { useEffect, useRef } from 'react';
import { LayoutServiceData } from '@sitecore-jss/sitecore-jss-nextjs';

interface LayoutProps {
  layoutData: LayoutServiceData;
}

const LMAPageLayout = ({ layoutData }: LayoutProps) => {
  const theme = useTheme();
  const { route, context } = layoutData.sitecore;
  const isFooterExist = !isEmpty(route?.placeholders['jss-footer']);
  const isHeaderExist = !isEmpty(route?.placeholders['jss-header']);
  const pageContainerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const primaryFontFamilyType =
      (context?.siteSettings as any)?.fontFamily?.fields?.primaryFontFamilyType?.value ||
      'Proxima Nova';
    const primaryFontUrl = (context?.siteSettings as any)?.fontFamily?.fields?.primaryFontUrl
      ?.value;
    const primaryFontUrlSemiBold = (context?.siteSettings as any)?.fontFamily?.fields
      ?.primaryFontUrlSemiBold?.value;
    const primaryFontUrlRegular = (context?.siteSettings as any)?.fontFamily?.fields
      ?.primaryFontUrlRegular?.value;
    const primaryFontUrlBold = (context?.siteSettings as any)?.fontFamily?.fields
      ?.primaryFontUrlBold?.value;
    const secondaryFontFamilyType =
      (context?.siteSettings as any)?.fontFamily?.fields?.secondaryFontFamilyType?.value ||
      'Proxima Nova';
    const secondaryFontUrl = (context?.siteSettings as any)?.fontFamily?.fields?.secondaryFontUrl
      ?.value;
    const secondaryFontUrlSemiBold = (context?.siteSettings as any)?.fontFamily?.fields
      ?.secondaryFontUrlSemiBold?.value;
    const secondaryFontUrlRegular = (context?.siteSettings as any)?.fontFamily?.fields
      ?.secondaryFontUrlRegular?.value;
    const secondaryFontUrlBold = (context?.siteSettings as any)?.fontFamily?.fields
      ?.secondaryFontUrlBold?.value;

    // Create a new style element for dynamic fonts
    const injectFontStyles = () => {
      const style = document.createElement('style');
      style.type = 'text/css';
      style.innerHTML = `
          @font-face {
            font-family: '${primaryFontFamilyType}';
            src: ${primaryFontUrl}
            font-display: swap;
          }
          @font-face {
            font-family: '${primaryFontFamilyType} SemiBold';
            src: ${primaryFontUrlSemiBold}
            font-display: swap;
          }
          @font-face {
            font-family: '${primaryFontFamilyType} Bold';
            src: ${primaryFontUrlBold}
            font-display: swap;
          }
          @font-face {
            font-family: '${primaryFontFamilyType} Regular';
            src: ${primaryFontUrlRegular}
            font-display: swap;
          }
   
          @font-face {
            font-family: '${secondaryFontFamilyType}';
            src: ${secondaryFontUrl}
            font-display: swap;
          }
          @font-face {
            font-family: '${secondaryFontFamilyType} SemiBold';
            src: ${secondaryFontUrlSemiBold}
            font-display: swap;
          }
          @font-face {
            font-family: '${secondaryFontFamilyType} Bold';
            src: ${secondaryFontUrlBold}
            font-display: swap;
          }
          @font-face {
            font-family: '${secondaryFontFamilyType} Regular';
            src: ${secondaryFontUrlRegular}
            font-display: swap;
          }
   
          @font-face {
            font-family: 'Primary';
            src: ${primaryFontUrl}
            font-display: swap;
          }
          @font-face {
            font-family: 'Primary SemiBold';
            src: ${primaryFontUrlSemiBold}
            font-display: swap;
          }
          @font-face {
            font-family: 'Primary Bold';
            src: ${primaryFontUrlBold}
            font-display: swap;
          }
          @font-face {
            font-family: 'Primary Regular, ';
            src: ${primaryFontUrlRegular}
            font-display: swap;
          }
          @font-face {
            font-family: 'Secondary';
            src: ${secondaryFontUrl}
            font-display: swap;
          }
          @font-face {
            font-family: 'Secondary SemiBold';
            src: ${secondaryFontUrlSemiBold}
            font-display: swap;
          }
          @font-face {
            font-family: 'Secondary Bold';
            src: ${secondaryFontUrlBold}
            font-display: swap;
          }
          @font-face {
            font-family: 'Secondary Regular';
            src: ${secondaryFontUrlRegular}
            font-display: swap;
          }

          /* LMA: force font-smoothing so colors render correctly on all OS/browser combos */
          body,
          .MuiTableCell-root,
          .MuiTypography-root,
          .MuiInputBase-root {
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
          }
        `;
      // Append the style element to the head
      document.head.appendChild(style);
      // Set the CSS variable for font-family
      document.documentElement.style.setProperty('--font-family-base', primaryFontFamilyType);
      document.documentElement.style.setProperty(
        '--font-family-secondary',
        secondaryFontFamilyType
      );
    };
    if ('requestIdleCallback' in window) {
      requestIdleCallback(injectFontStyles, { timeout: 200 });
    } else {
      setTimeout(injectFontStyles, 200);
    }
  }, [context]);

  return (
    <>
      {route && isHeaderExist && (
        <div>
          <StAppBarWrapper>
            <AppBar
              position={'fixed'}
              sx={{
                bgcolor: 'appBarColor.main',
                zIndex: (t: import('@mui/material/styles').Theme) => t.zIndex.drawer + 1,
                [theme.breakpoints.up('md')]: {
                  position:
                    isEditorActive() || context?.pageState === 'preview' ? 'static' : 'fixed',
                },
                boxShadow: 'unset',
                borderBottom: '1px solid #d9d9d9',
              }}
            >
              <Placeholder name="jss-header" rendering={route} disableSuspense={true} />
            </AppBar>
            <Toolbar />
          </StAppBarWrapper>
        </div>
      )}

      <main className="container" ref={pageContainerRef}>
        {route && (
          <Placeholder
            name="jss-main"
            rendering={route}
            pageContainerRef={pageContainerRef}
            isFooterExist={isFooterExist}
            disableSuspense={true}
          />
        )}
      </main>
      {isFooterExist && (
        <footer id="footer">
          {route && <Placeholder name="jss-footer" rendering={route} disableSuspense={true} />}
        </footer>
      )}
    </>
  );
};

export default LMAPageLayout;
